/**
 * calendarUtils.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Utilitários de calendário para o fluxo de agendamentos v2.
 * Funções puras — sem efeitos colaterais, sem dependências de estado React.
 *
 * @module calendarUtils
 */

import {
  WEEKDAYS_LONG,
  WEEKDAYS_SHORT,
  MONTHS,
  MONTHS_SHORT,
  SLOT_INTERVAL,
  DEFAULT_START_HOUR,
} from '../constants/calendarConstants';

// ── Geração de datas ──────────────────────────────────────────────────────────

/**
 * Retorna array de 7 datas (Dom–Sáb) para a semana baseada em um offset.
 *
 * @param {number} [offsetWeeks=0] - Offset em semanas a partir da semana atual
 * @returns {Date[]} Array de 7 datas representando a semana
 */
export function getWeekDates(offsetWeeks = 0) {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Dom
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - dayOfWeek + offsetWeeks * 7);
  sunday.setHours(0, 0, 0, 0);

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sunday);
    d.setDate(sunday.getDate() + i);
    return d;
  });
}

/**
 * Retorna array com uma única data baseada em um offset de dias.
 *
 * @param {number} [offsetDays=0] - Offset em dias a partir de hoje
 * @returns {Date[]} Array com a data resultante
 */
export function getDayDates(offsetDays = 0) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const result = new Date(today);
  result.setDate(today.getDate() + offsetDays);
  return [result];
}

// ── Formatação de datas ───────────────────────────────────────────────────────

/**
 * Formata data para exibição no header do calendário.
 * Exemplo: "Quinta-feira • 22 Maio"
 *
 * @param {Date} date - Data a formatar
 * @returns {string} Data formatada
 */
export function formatDateHeader(date) {
  const weekday = WEEKDAYS_LONG[date.getDay()];
  const day = date.getDate();
  const month = MONTHS[date.getMonth()];
  return `${weekday} • ${day} ${month}`;
}

/**
 * Formata data no formato curto "DD/MM".
 * Exemplo: "22/05"
 *
 * @param {Date} date - Data a formatar
 * @returns {string} Data no formato DD/MM
 */
export function formatDateShort(date) {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
}

/**
 * Formata hora no formato "HH:MM".
 * Exemplo: "14:00"
 *
 * @param {Date} date - Data/hora a formatar
 * @returns {string} Hora no formato HH:MM
 */
export function formatTime(date) {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Formata intervalo de tempo.
 * Exemplo: "14:00 → 14:50"
 *
 * @param {Date} startDate - Data/hora de início
 * @param {number} durationMinutes - Duração em minutos
 * @returns {string} Intervalo formatado
 */
export function formatTimeRange(startDate, durationMinutes) {
  const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
  return `${formatTime(startDate)} → ${formatTime(endDate)}`;
}

/**
 * Formata o label do período exibido no header de navegação.
 * Semana: "18 – 24 Maio 2026"
 * Dia: "Sexta-feira, 22 Maio 2026"
 *
 * @param {Date[]} dates - Array de datas visíveis
 * @param {'daily'|'weekly'|'monthly'} viewMode - Modo de visualização
 * @returns {string} Label do período
 */
export function formatPeriodLabel(dates, viewMode) {
  if (!dates || dates.length === 0) return '';

  if (viewMode === 'daily') {
    const d = dates[0];
    const weekday = WEEKDAYS_LONG[d.getDay()];
    const day = d.getDate();
    const month = MONTHS[d.getMonth()];
    const year = d.getFullYear();
    return `${weekday}, ${day} ${month} ${year}`;
  }

  if (viewMode === 'weekly') {
    const first = dates[0];
    const last = dates[dates.length - 1];
    const sameMonth = first.getMonth() === last.getMonth();
    const sameYear = first.getFullYear() === last.getFullYear();

    if (sameMonth) {
      return `${first.getDate()} – ${last.getDate()} ${MONTHS[first.getMonth()]} ${first.getFullYear()}`;
    }
    if (sameYear) {
      return `${first.getDate()} ${MONTHS_SHORT[first.getMonth()]} – ${last.getDate()} ${MONTHS_SHORT[last.getMonth()]} ${first.getFullYear()}`;
    }
    return `${first.getDate()} ${MONTHS_SHORT[first.getMonth()]} ${first.getFullYear()} – ${last.getDate()} ${MONTHS_SHORT[last.getMonth()]} ${last.getFullYear()}`;
  }

  if (viewMode === 'monthly') {
    const d = dates[0];
    return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  }

  return '';
}

// ── Verificações de data ──────────────────────────────────────────────────────

/**
 * Verifica se uma data é hoje.
 *
 * @param {Date} date - Data a verificar
 * @returns {boolean}
 */
export function isToday(date) {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

/**
 * Verifica se uma data está no passado (antes de hoje).
 *
 * @param {Date} date - Data a verificar
 * @returns {boolean}
 */
export function isPast(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d < today;
}

/**
 * Verifica se duas datas representam o mesmo dia.
 *
 * @param {Date} a - Primeira data
 * @param {Date} b - Segunda data
 * @returns {boolean}
 */
export function isSameDay(a, b) {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

// ── Conversão de posição ──────────────────────────────────────────────────────

/**
 * Calcula a posição Y em pixels para um horário dado a altura do slot por hora.
 *
 * @param {number} hours - Hora (0–23)
 * @param {number} minutes - Minutos (0–59)
 * @param {number} slotHeight - Altura em pixels por hora
 * @param {number} [startHour=DEFAULT_START_HOUR] - Hora de início do calendário
 * @returns {number} Posição Y em pixels
 */
export function timeToPixels(hours, minutes, slotHeight, startHour = DEFAULT_START_HOUR) {
  const totalMinutes = (hours - startHour) * 60 + minutes;
  return (totalMinutes / 60) * slotHeight;
}

/**
 * Calcula o horário a partir de uma posição Y em pixels.
 *
 * @param {number} y - Posição Y em pixels
 * @param {number} slotHeight - Altura em pixels por hora
 * @param {number} [startHour=DEFAULT_START_HOUR] - Hora de início do calendário
 * @returns {{ hours: number, minutes: number }} Objeto com horas e minutos
 */
export function pixelsToTime(y, slotHeight, startHour = DEFAULT_START_HOUR) {
  const totalMinutes = Math.round((y / slotHeight) * 60);
  const hours = startHour + Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours, minutes };
}

/**
 * Arredonda uma data para o intervalo de slot mais próximo.
 *
 * @param {Date} date - Data a arredondar
 * @param {number} [intervalMinutes=SLOT_INTERVAL] - Intervalo em minutos
 * @returns {Date} Data arredondada
 */
export function roundToSlotInterval(date, intervalMinutes = SLOT_INTERVAL) {
  const result = new Date(date);
  const minutes = result.getMinutes();
  const rounded = Math.round(minutes / intervalMinutes) * intervalMinutes;
  result.setMinutes(rounded, 0, 0);
  return result;
}

// ── Geração de slots ──────────────────────────────────────────────────────────

/**
 * @typedef {Object} TimeSlot
 * @property {number} hour - Hora (0–23)
 * @property {number} minute - Minuto (0 ou 30)
 * @property {string} label - Label formatado "HH:MM"
 * @property {boolean} isHourMark - Se é o início de uma hora cheia
 */

/**
 * Retorna array de slots de tempo para um dia.
 *
 * @param {number} [startHour=DEFAULT_START_HOUR] - Hora de início
 * @param {number} [endHour=20] - Hora de fim (exclusiva)
 * @param {number} [intervalMinutes=SLOT_INTERVAL] - Intervalo em minutos
 * @returns {TimeSlot[]} Array de slots
 */
export function getTimeSlots(startHour = DEFAULT_START_HOUR, endHour = 20, intervalMinutes = SLOT_INTERVAL) {
  const slots = [];
  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      const label = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      slots.push({
        hour,
        minute,
        label,
        isHourMark: minute === 0,
      });
    }
  }
  return slots;
}

// ── Sobreposição de agendamentos ──────────────────────────────────────────────

/**
 * Verifica se dois agendamentos se sobrepõem no tempo.
 *
 * @param {{ date: string, duration: number }} apt1 - Primeiro agendamento
 * @param {{ date: string, duration: number }} apt2 - Segundo agendamento
 * @returns {boolean} True se houver sobreposição
 */
export function appointmentsOverlap(apt1, apt2) {
  const start1 = new Date(apt1.date).getTime();
  const end1 = start1 + apt1.duration * 60 * 1000;
  const start2 = new Date(apt2.date).getTime();
  const end2 = start2 + apt2.duration * 60 * 1000;

  return start1 < end2 && start2 < end1;
}

/**
 * @typedef {Object} AppointmentGroup
 * @property {Array} appointments - Agendamentos do grupo
 * @property {number} columns - Número de colunas necessárias
 */

/**
 * Agrupa agendamentos sobrepostos para layout em colunas.
 * Retorna array de grupos, cada grupo com seus agendamentos e
 * a coluna atribuída a cada um.
 *
 * @param {Array<{ id: string, date: string, duration: number }>} appointments
 * @returns {Array<{ appointment: object, column: number, totalColumns: number }>}
 */
export function groupOverlappingAppointments(appointments) {
  if (!appointments || appointments.length === 0) return [];

  // Ordena por hora de início
  const sorted = [...appointments].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const result = [];
  const groups = []; // grupos de sobreposição

  for (const apt of sorted) {
    // Encontra grupo onde este agendamento se sobrepõe
    let placed = false;
    for (const group of groups) {
      const overlapsAny = group.some(item => appointmentsOverlap(item.appointment, apt));
      if (overlapsAny) {
        // Encontra coluna disponível
        const usedColumns = new Set(group.map(item => item.column));
        let col = 0;
        while (usedColumns.has(col)) col++;
        group.push({ appointment: apt, column: col });
        placed = true;
        break;
      }
    }
    if (!placed) {
      groups.push([{ appointment: apt, column: 0 }]);
    }
  }

  // Calcula totalColumns para cada grupo e achata
  for (const group of groups) {
    const totalColumns = Math.max(...group.map(item => item.column)) + 1;
    for (const item of group) {
      result.push({ ...item, totalColumns });
    }
  }

  return result;
}

/**
 * Converte string "YYYY-MM-DD HH:mm:ss" para objeto Date.
 *
 * @param {string} dateStr - String de data no formato do modelo
 * @returns {Date}
 */
export function parseAppointmentDate(dateStr) {
  if (!dateStr) return new Date();
  // Substitui espaço por T para compatibilidade com Date constructor
  return new Date(dateStr.replace(' ', 'T'));
}
